import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _assertThisInitialized from "@babel/runtime/helpers/assertThisInitialized";
import _inherits from "@babel/runtime/helpers/inherits";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _taggedTemplateLiteral from "@babel/runtime/helpers/taggedTemplateLiteral";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _excluded = ["selectedDate", "onChange", "activeStartDate", "className", "errorText", "label", "error", "size", "readOnly", "disabled", "width", "format", "bodyConfig", "typescale", "hoverState", "readOnlyBorders", "overflowEllipsis", "calculateInputPadding", "minWidth", "maxWidth", "iconConfig", "labelMarginBottom", "iconPosition", "colorConfig", "required", "focusState", "placeHolderText", "styleConfig", "Calendar", "minDate", "maxDate", "pastDatesDisabled", "tooltipTitle", "tooltipContent", "Tooltip", "inactiveDates", "activeDates", "id", "helperText", "helperTextPlacement", "Body", "Micro", "alwaysOpen", "inputId", "inverted"];

var _defaultProps, _templateObject, _templateObject2;

function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }

function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import cuid from 'cuid';
import { calculateRem } from '@uie/javascript-style-helpers';
import UNSAFE_SetEnvRef from '@uie/set-env-ref';
import { InputField } from '../InputField';
import Icon from '@vds-core/icons';
import { Calendar as DefaultCalendar } from '@vds-core/calendars';
import { colors } from '@vds-core/theme';
import { TypographyConfig } from '@vds-core/typography';
import { withVDSManager } from '@vds-core/utilities';
import { showError } from '@vds-core/form-elements';
import { formatDate } from '../../utils';
var propTypes = {
  /**
   * The beginning of a period that shall be displayed by the Calendar. If no value is provided, the current date (new Date()) will be used.
   */
  selectedDate: PropTypes.instanceOf(Date),

  /**
   * @ignore
   */
  className: PropTypes.string,

  /**
   * Boolean or a Function that returns a boolean value that determines if component should show the error state/error message. Function receives an object of type DATE on input change.
   * @note If this is provided, you are responsible for controlling all errors for this component. Otherwise, if the Input is required, this component will throw an error if the component is blurred and does not contain any input.
   */
  error: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),

  /**
   *  Determines what event this component will check for an error, options are 'onBlur' or 'onChange'.
   */
  errorEvent: PropTypes.oneOf(['blur', 'change', 'blurAndChange']),

  /**
   * @deprecated
   */
  size: PropTypes.oneOf(['normal', 'small', 'large']),

  /**
   * Callback used to get the selected date from DatePicker.
   */
  onChange: PropTypes.func,

  /**
   * If provided and in error state, the error text will be displayed under the calendar input.
   */
  errorText: PropTypes.string,

  /**
   * If provided, this will render as the label text of the DatePicker input.
   */
  label: PropTypes.string,

  /**
   * @ignore
   */
  readOnly: PropTypes.bool,

  /**
   * If provided, the Input will be rendered in the Disabled State.
   */
  disabled: PropTypes.bool,

  /**
   * If provided, will override the default sizing widths for the small and large datepicker input.
   */
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /**
   * The format of the DatePicker field, can be 'MM/DD/YY', 'MM/DD/YYYY', or 'Month, Date, Year'.
   */
  format: PropTypes.oneOf(['MM/DD/YY', 'Month, Date, Year', 'MM/DD/YYYY']),

  /**
   * @deprecated selectedDate
   */
  activeStartDate: PropTypes.instanceOf(Date),

  /**
   * @deprecated onChange
   */
  handleDatePick: PropTypes.func,

  /**
   * @ignore
   */
  required: PropTypes.bool,

  /**
   * @ignore - config for passing through other colors
   */
  colorConfig: PropTypes.object,

  /**
   * @ignore - config for passing though typography
   */
  bodyConfig: PropTypes.object,

  /**
   * @ignore - passes through typescale via sting if there's more than one option [example 'VDS' instead of 'MVP']
   */
  typescale: PropTypes.string,

  /**
   * @ignore - used for state in showing and hiding error icon on focus of the input
   */
  iconFocusState: PropTypes.bool,

  /**
   * @ignore - passes Icon data through
   */
  iconConfig: PropTypes.object,

  /**
   * @ignore - moves icon to the right side of the input label
   */
  iconPosition: PropTypes.string,

  /**
   * @ignore - adds max-width to input field
   */
  maxWidth: PropTypes.string,

  /**
   * @ignore - adds min-width to input field
   */
  minWidth: PropTypes.string,

  /**
   * @ignore - adds hover state to input field
   */
  hoverState: PropTypes.bool,

  /**
   * @ignore - padding on the inside of the input field takes in params of iconFocusState and error
   */
  calculateInputPadding: PropTypes.func,

  /**
   * @ignore - spacing between the label and the input field
   */
  labelMarginBottom: PropTypes.string,

  /**
   * @ignore - defines whether text trancates with ellipsis or not
   */
  overflowEllipsis: PropTypes.bool,

  /**
   * @ignore
   */
  readOnlyBorders: PropTypes.bool,

  /**
   * @ignore
   */
  Calendar: PropTypes.func,

  /**
   * If provided, the calendar will allow a selection to be made from this date forward. Defaults to today.
   */
  minDate: PropTypes.instanceOf(Date),

  /**
   * If provided, dates will be selectable up until this date.
   */
  maxDate: PropTypes.instanceOf(Date),

  /**
   * @deprecated minDate
   */
  pastDatesDisabled: PropTypes.bool,

  /**
   * @ignore - number representing height of input field
   */
  inputHeight: PropTypes.string,

  /**
   * Allows a unique ID to be passed to the component.
   */
  id: PropTypes.string,

  /**
   * Allows a unique ID to be passed to the input element.
   */
  inputId: PropTypes.string,

  /**
   * @ignore If passed, the Tooltip will render
   */
  Tooltip: PropTypes.func,

  /**
   * Enable specific dates. Pass an array of strings in date format.
   * @note Formats accepted: MM/DD/YYYY, MM/DD/YY, Sep 24 2020, 25 Sep 2020, 25 September 2020, September 24 2020, 24-09-2020, 2020-09-24. All other dates will be inactive.
   */
  activeDates: PropTypes.array,

  /**
   * Disable specific dates. Pass an array of strings in date format.
   * @note Formats accepted: MM/DD/YYYY, MM/DD/YY, Sep 24 2020, 25 Sep 2020, 25 September 2020, September 24 2020, 24-09-2020, 2020-09-24. All other dates will be active.
   */
  inactiveDates: PropTypes.array,

  /**
   * If provided, the datepicker will be rendered in the inverted state.
   */
  inverted: PropTypes.bool
};
var defaultProps = (_defaultProps = {
  className: null,
  error: undefined,
  errorEvent: 'blur',
  size: 'normal',
  onChange: function onChange() {},
  errorText: 'Error Text',
  label: '',
  disabled: false,
  readOnly: false,
  width: '100%',
  format: 'Month, Date, Year',
  inputHeight: calculateRem(42),
  pastDatesDisabled: true,
  minDate: new Date(),
  maxDate: undefined,
  colorConfig: colors,
  typescale: TypographyConfig.getTypescale(),
  inactiveDates: null,
  activeDates: null,
  Calendar: DefaultCalendar
}, _defineProperty(_defaultProps, "pastDatesDisabled", false), _defineProperty(_defaultProps, "Tooltip", undefined), _defineProperty(_defaultProps, "inverted", false), _defaultProps);
var CALENDAR_HEIGHT_TIGHT = 320;
var CALENDAR_HEIGHT = 328;
var CALENDAR_PADDING = 4;

var _getCalendarHeight = function _getCalendarHeight() {
  return window.innerWidth >= 360 ? CALENDAR_HEIGHT : CALENDAR_HEIGHT_TIGHT;
};

var DatePickerContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "DatePickerContainer",
  componentId: "sc-rpyx36-0"
})(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  position: relative;\n  width: ", ";\n\n  input:required {\n    box-shadow: none;\n  }\n  max-width: ", ";\n  min-width: ", ";\n  ", ";\n"])), function (_ref) {
  var width = _ref.width;
  return typeof width === 'number' ? calculateRem(width) : width;
}, function (_ref2) {
  var maxWidth = _ref2.maxWidth;
  return calculateRem(maxWidth);
}, function (_ref3) {
  var minWidth = _ref3.minWidth;
  return calculateRem(minWidth);
}, function (_ref4) {
  var height = _ref4.height;
  return "height: ".concat(calculateRem(height));
});
var CalendarIconWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "CalendarIconWrapper",
  componentId: "sc-rpyx36-1"
})(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n  display: inline-flex;\n  top: ", ";\n  left: ", ";\n  position: absolute;\n  z-index: 1;\n  cursor: ", ";\n  &:hover + span {\n    ", ";\n  }\n"])), function (_ref5) {
  var typescale = _ref5.typescale;
  return typescale === 'VDS' ? '12px' : '11px';
}, function (_ref6) {
  var readOnly = _ref6.readOnly,
      readOnlyBorders = _ref6.readOnlyBorders;
  return readOnly && !readOnlyBorders ? 0 : readOnlyBorders ? '12px' : '16px';
}, function (_ref7) {
  var readOnly = _ref7.readOnly,
      disabled = _ref7.disabled;
  return !readOnly && !disabled && "pointer";
}, function (_ref8) {
  var readOnly = _ref8.readOnly,
      disabled = _ref8.disabled,
      inverted = _ref8.inverted;
  return !readOnly && !disabled && "\n      border: ".concat(calculateRem(1), " solid ").concat(inverted ? colors.white : colors.black, ";\n  ");
});
var outsideActionEvents = ['mousedown', 'focusin', 'touchstart'];

var DatePicker = /*#__PURE__*/function (_React$Component) {
  _inherits(DatePicker, _React$Component);

  var _super = _createSuper(DatePicker);

  function DatePicker(props) {
    var _this2;

    _classCallCheck(this, DatePicker);

    _this2 = _super.call(this, props);

    _defineProperty(_assertThisInitialized(_this2), "refKey", UNSAFE_SetEnvRef());

    _defineProperty(_assertThisInitialized(_this2), "onOutsideAction", function (event) {
      var target = event.target;
      var _this2$props = _this2.props,
          required = _this2$props.required,
          error = _this2$props.error;

      if (_this2.calendarRef && !_this2.calendarRef.contains(target)) {
        _this2.closeCalendar();

        var value = _this2.calendarRef.querySelector('input').value;

        if (!value && required && error === undefined) {
          _this2.setState({
            emptyError: true
          });
        }

        if (_this2.props.errorEvent === 'blur' || _this2.props.errorEvent === 'blurAndChange') {
          var _this = _assertThisInitialized(_this2);

          showError(value, _this);
        }
      }
    });

    _defineProperty(_assertThisInitialized(_this2), "componentDidMount", function () {
      var format = _this2.props.format;

      if (_this2.props.activeStartDate || _this2.props.selectedDate) {
        _this2.setState({
          date: formatDate(_this2.props.activeStartDate || _this2.props.selectedDate, format)
        });
      }

      _this2.handleOutsideActionListeners();

      _this2._calcHeight();

      _this2.calendarRef.addEventListener('resize', _this2._calcHeight);

      _this2.calendarRef.addEventListener('keydown', _this2._onEsc);
    });

    _defineProperty(_assertThisInitialized(_this2), "componentWillUnmount", function () {
      _this2.handleOutsideActionListeners(false);

      _this2.calendarRef.removeEventListener('resize', _this2._calcHeight);

      _this2.calendarRef.removeEventListener('keydown', _this2._onEsc);
    });

    _defineProperty(_assertThisInitialized(_this2), "closeCalendar", function () {
      var callback = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;

      _this2.setState({
        isOpen: false
      }, function () {
        callback && _this2.calendarRef.querySelector('input').focus();
      });
    });

    _defineProperty(_assertThisInitialized(_this2), "openCalendar", function () {
      var callback = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;

      _this2.setState({
        isOpen: true
      }, function () {
        callback && _this2.calendarRef.querySelector('button[class^="react-calendar"]:not([disabled])').focus();
      });
    });

    _defineProperty(_assertThisInitialized(_this2), "onChange", function (date) {
      var _this2$props2 = _this2.props,
          size = _this2$props2.size,
          format = _this2$props2.format,
          handleDatePick = _this2$props2.handleDatePick,
          onChange = _this2$props2.onChange,
          errorEvent = _this2$props2.errorEvent;
      var formattedDate = formatDate(date, format);
      onChange && onChange(formattedDate);
      if (_this2.props.error === undefined) _this2.setState({
        emptyError: false
      });

      _this2.setState({
        date: formattedDate
      });

      if (typeof handleDatePick !== 'undefined') handleDatePick(formattedDate);

      _this2.closeCalendar(true);

      var _this = _assertThisInitialized(_this2);

      showError(formattedDate, _this);
    });

    _defineProperty(_assertThisInitialized(_this2), "_renderCalendarIcon", function () {
      var _this2$props3 = _this2.props,
          iconConfig = _this2$props3.iconConfig,
          readOnly = _this2$props3.readOnly,
          readOnlyBorders = _this2$props3.readOnlyBorders,
          label = _this2$props3.label,
          typescale = _this2$props3.typescale,
          disabled = _this2$props3.disabled,
          colorConfig = _this2$props3.colorConfig,
          inverted = _this2$props3.inverted;
      var iconColor = colorConfig.black;

      if (inverted) {
        if (disabled) iconColor = colorConfig.coolGray10;else iconColor = colorConfig.white;
      } else if (disabled) {
        iconColor = colorConfig.grayLight;
      } else if (!_this2.state.date && typescale !== 'VDS') {
        iconColor = colorConfig.coolGray6;
      }

      return /*#__PURE__*/React.createElement(CalendarIconWrapper, {
        typescale: typescale,
        readOnlyBorders: readOnlyBorders,
        readOnly: readOnly,
        disabled: disabled,
        label: label,
        inverted: inverted,
        onClick: readOnly ? function () {} : function () {
          return _this2.openCalendar();
        }
      }, /*#__PURE__*/React.createElement(Icon, {
        style: {
          position: 'relative'
        },
        data: iconConfig,
        tabIndex: -1,
        name: "calendar",
        size: "medium",
        color: iconColor
      }));
    });

    _defineProperty(_assertThisInitialized(_this2), "_calculateInputPadding", function () {
      var _this2$props4 = _this2.props,
          readOnly = _this2$props4.readOnly,
          locked = _this2$props4.locked,
          readOnlyBorders = _this2$props4.readOnlyBorders,
          calculateInputPadding = _this2$props4.calculateInputPadding;

      if (calculateInputPadding) {
        return calculateInputPadding;
      } else if ((readOnly || locked) && !readOnlyBorders) {
        return calculateRem(12, 16, 12, 32);
      } else {
        return calculateRem(12, 16, 12, 50);
      }
    });

    _defineProperty(_assertThisInitialized(_this2), "_onKeyDown", function (e) {
      e.keyCode !== 9 && e.preventDefault();

      if (e.keyCode === 13 || e.keyCode === 32) {
        _this2.openCalendar(true);
      }
    });

    _defineProperty(_assertThisInitialized(_this2), "_onKeyUp", function (e) {
      e.keyCode === 9 && _this2.openCalendar(true);
    });

    _defineProperty(_assertThisInitialized(_this2), "_onEsc", function (e) {
      if (e.keyCode === 27) {
        _this2.closeCalendar(true);
      }
    });

    _defineProperty(_assertThisInitialized(_this2), "_onFocusOut", function (e) {
      if (e.keyCode === 9) {
        _this2.closeCalendar();
      }
    });

    _defineProperty(_assertThisInitialized(_this2), "_calcHeight", function () {
      var inputHeight = (_this2.props.label ? _this2.calendarRef.querySelector('label').getBoundingClientRect().height + CALENDAR_PADDING : 0) + _this2.calendarRef.querySelector('input').getBoundingClientRect().height + CALENDAR_PADDING;

      if (_this2.props.alwaysOpen) {
        _this2.setState({
          calendarTop: inputHeight,
          datePickerHeight: _getCalendarHeight() + inputHeight
        });
      } else {
        _this2.setState({
          calendarTop: inputHeight
        });
      }
    });

    _this2.state = {
      isOpen: _this2.props.alwaysOpen && !_this2.props.disabled ? _this2.props.alwaysOpen : false,
      error: typeof _this2.props.error === 'function' ? false : _this2.props.error,
      // If error passes a function, then the error state will initially be set to false
      emptyError: undefined,
      datePickerHeight: 0,
      calendarHeight: CALENDAR_HEIGHT
    };
    return _this2;
  }

  _createClass(DatePicker, [{
    key: "handleOutsideActionListeners",
    value: function handleOutsideActionListeners(shouldListen) {
      var _this3 = this;

      var isOpen = this.state.isOpen;
      var shouldListenWithFallback = typeof shouldListen !== 'undefined' ? shouldListen : isOpen;
      var fnName = shouldListenWithFallback ? 'addEventListener' : 'removeEventListener';
      outsideActionEvents.forEach(function (eventName) {
        document[fnName](eventName, _this3.onOutsideAction);
      });
    }
  }, {
    key: "componentDidUpdate",
    value: function componentDidUpdate(prevProps, prevState) {
      var _this$props = this.props,
          size = _this$props.size,
          format = _this$props.format,
          selectedDate = _this$props.selectedDate,
          error = _this$props.error,
          alwaysOpen = _this$props.alwaysOpen,
          disabled = _this$props.disabled;

      if (size !== prevProps.size || format !== prevProps.format) {
        var formattedDate = formatDate(this.state.date, format);
        this.setState({
          date: formattedDate
        });
      } else if (selectedDate !== prevProps.selectedDate) {
        var _formattedDate = formatDate(selectedDate, format);

        this.setState({
          date: _formattedDate
        });
      }

      if (prevProps.error !== error) {
        this.setState({
          error: error
        });
      }

      if (prevProps.alwaysOpen !== alwaysOpen || prevProps.disabled !== disabled) {
        this.setState({
          isOpen: disabled ? false : alwaysOpen
        });
      }

      if (this.state.isOpen !== prevState.isOpen) {
        this.handleOutsideActionListeners();
      }

      if (error === undefined && prevProps.emptyError !== this.props.emptyError) {
        this.setState({
          emptyError: emptyError
        });
      }

      if (error === undefined && prevProps.required !== this.props.required) {
        this.setState({
          emptyError: false
        });
      }

      if (prevProps.alwaysOpen !== this.props.alwaysOpen || prevProps.label !== this.props.label || prevProps.helperText !== this.props.helperText || prevProps.helperTextPlacement !== this.props.helperTextPlacement) {
        this._calcHeight();
      }
    }
  }, {
    key: "render",
    value: function render() {
      var _this4 = this;

      var _this$props2 = this.props,
          selectedDateProp = _this$props2.selectedDate,
          onChange = _this$props2.onChange,
          activeStartDate = _this$props2.activeStartDate,
          className = _this$props2.className,
          errorText = _this$props2.errorText,
          label = _this$props2.label,
          errorProp = _this$props2.error,
          size = _this$props2.size,
          readOnly = _this$props2.readOnly,
          disabled = _this$props2.disabled,
          width = _this$props2.width,
          format = _this$props2.format,
          bodyConfig = _this$props2.bodyConfig,
          typescale = _this$props2.typescale,
          hoverState = _this$props2.hoverState,
          readOnlyBorders = _this$props2.readOnlyBorders,
          overflowEllipsis = _this$props2.overflowEllipsis,
          calculateInputPadding = _this$props2.calculateInputPadding,
          minWidth = _this$props2.minWidth,
          maxWidth = _this$props2.maxWidth,
          iconConfig = _this$props2.iconConfig,
          labelMarginBottom = _this$props2.labelMarginBottom,
          iconPosition = _this$props2.iconPosition,
          colorConfig = _this$props2.colorConfig,
          required = _this$props2.required,
          focusState = _this$props2.focusState,
          placeHolderText = _this$props2.placeHolderText,
          styleConfig = _this$props2.styleConfig,
          Calendar = _this$props2.Calendar,
          minDate = _this$props2.minDate,
          maxDate = _this$props2.maxDate,
          pastDatesDisabled = _this$props2.pastDatesDisabled,
          tooltipTitle = _this$props2.tooltipTitle,
          tooltipContent = _this$props2.tooltipContent,
          Tooltip = _this$props2.Tooltip,
          inactiveDates = _this$props2.inactiveDates,
          activeDates = _this$props2.activeDates,
          id = _this$props2.id,
          helperText = _this$props2.helperText,
          helperTextPlacement = _this$props2.helperTextPlacement,
          Body = _this$props2.Body,
          Micro = _this$props2.Micro,
          alwaysOpen = _this$props2.alwaysOpen,
          inputId = _this$props2.inputId,
          inverted = _this$props2.inverted,
          otherProps = _objectWithoutProperties(_this$props2, _excluded);

      var _this$state = this.state,
          isOpen = _this$state.isOpen,
          date = _this$state.date,
          calendarTop = _this$state.calendarTop,
          datePickerHeight = _this$state.datePickerHeight,
          errorState = _this$state.error;
      var error = errorState || typeof errorProp === 'boolean' && errorProp;
      var dateId = inputId ? inputId : cuid();
      var selectedDate = error ? null : activeStartDate ? activeStartDate : selectedDateProp;
      return /*#__PURE__*/React.createElement(DatePickerContainer, _extends({
        id: id,
        className: className,
        width: width,
        styleConfig: styleConfig,
        minWidth: minWidth,
        maxWidth: maxWidth,
        height: alwaysOpen ? datePickerHeight : undefined,
        "data-testid": "date-test-id"
      }, _defineProperty({}, this.refKey, function (elem) {
        return _this4.calendarRef = elem;
      })), /*#__PURE__*/React.createElement(InputField, _extends({
        overflowEllipsis: overflowEllipsis,
        focusState: focusState,
        iconPosition: iconPosition,
        required: required,
        minWidth: minWidth,
        maxWidth: maxWidth,
        typescale: typescale,
        iconConfig: iconConfig,
        hoverState: hoverState,
        bodyConfig: bodyConfig,
        readOnlyBorders: readOnlyBorders,
        labelMarginBottom: labelMarginBottom,
        color: colorConfig,
        width: width,
        label: label,
        disabled: disabled,
        readOnly: readOnly,
        error: error,
        errorText: errorText,
        onClick: readOnly ? function () {} : function () {
          return _this4.openCalendar();
        },
        onKeyDown: this._onKeyDown,
        onKeyUp: this._onKeyUp,
        placeholder: 'Select Date',
        pickerSize: size,
        value: date || '',
        isOpen: isOpen,
        autoComplete: "off",
        Tooltip: Tooltip,
        tooltipTitle: tooltipTitle,
        tooltipContent: tooltipContent,
        helperText: helperText,
        helperTextPlacement: helperTextPlacement,
        Body: Body,
        Micro: Micro,
        inputId: dateId
      }, otherProps, {
        renderInputIcon: this._renderCalendarIcon,
        type: "calendar",
        calculateInputPadding: this._calculateInputPadding,
        emptyError: this.state.emptyError,
        inverted: inverted,
        caretColor: "transparent",
        alwaysOpenCalendar: disabled ? false : alwaysOpen
      })), /*#__PURE__*/React.createElement(Calendar, {
        datePicker: true,
        alwaysOpen: disabled ? false : alwaysOpen,
        Calendar: Calendar,
        selectedDate: selectedDate,
        isOpen: isOpen,
        onChange: this.onChange,
        minDate: minDate,
        maxDate: maxDate,
        pastDatesDisabled: pastDatesDisabled,
        inactiveDates: inactiveDates,
        activeDates: activeDates,
        inverted: inverted,
        calendarTop: calendarTop
      }));
    }
  }]);

  return DatePicker;
}(React.Component);

DatePicker.propTypes = propTypes;
DatePicker.defaultProps = defaultProps;
export default withVDSManager(DatePicker);